import React, { useEffect, useRef, useState } from "react";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import ConfirmModal from "../../common/ConfirmModal";
import { openAuthModal } from "../../common/authmodal/authmodalservice";
import { fetchRoleLookup, isRoleGuid, resolveRecipientRoleIds } from "../../../services/lookupService";
import { useUserData } from "../../../hooks/useUserData";
import { canEditMilestone } from "./isfPermissionUtils";
import {
  DEFAULT_NOTIFICATION_RULES,
  createMilestoneDraftVersion,
  extractMilestoneActionResult,
  extractMilestoneApiMessage,
  resolveMilestoneDuplicateCodeError,
  fetchMilestoneById,
  fetchMilestoneLookup,
  fetchMilestoneVersionHistory,
  normalizeMilestoneDetail,
  normalizeMilestoneLookup,
  normalizeMilestoneVersionHistory,
  parseTags,
  publishMilestone,
  statusBadgeClass,
  updateMilestone,
  buildMilestoneSavePayload,
  validateMilestoneNotifications,
  validateMilestoneDefaultFolder,
  validateMilestoneAnchor,
  validateMilestoneUploaderRoles,
  validateMilestoneRoleSelection,
  NOTIFICATION_ROLE_FALLBACK,
  resolveDefaultPiRoleId,
} from "./isfMilestoneService";
import {
  fetchFolderLibrary,
  folderLibraryToOptions,
  isFolderLibrarySuccess,
  normalizeFolderCategory,
  normalizeFolderLibraryList,
} from "./isfFolderLibraryService";
import {
  emptyMilestoneForm,
  normalizeMilestoneNotificationRow,
  formatMilestoneOffsetForForm,
} from "./milestoneLibraryShared";
import MilestoneLibraryForm from "./MilestoneLibraryForm";
import MilestoneRecordsGrid from "./MilestoneRecordsGrid";
import {
  MilestoneUsagePanel,
  MilestoneVersionHistoryPanel,
} from "./MilestoneUsageVersionPanel";
import { resolveFolderCategoryForName } from "./MilestoneFolderSelect";
import "./milestoneLibrary.scss";

const MilestoneEditorModal = ({
  milestoneId,
  lockedCategory = null,
  onClose,
  onChanged,
  onNavigateMilestone,
}) => {
  const lock = lockedCategory
    ? normalizeFolderCategory(lockedCategory)
    : null;
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();

  const [form, setForm] = useState(emptyMilestoneForm());
  const [versionHistory, setVersionHistory] = useState([]);
  const [errors, setErrors] = useState({});
  const [tagInput, setTagInput] = useState("");
  const [milestoneLookup, setMilestoneLookup] = useState([]);
  const [notificationRoles, setNotificationRoles] = useState(NOTIFICATION_ROLE_FALLBACK);
  const [notificationRolesLoaded, setNotificationRolesLoaded] = useState(false);
  const [folderOptions, setFolderOptions] = useState([]);
  const [loadAttempted, setLoadAttempted] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, action: null });
  const leftPanelRef = useRef(null);
  const [leftPanelHeight, setLeftPanelHeight] = useState(null);

  const canManage = userData && canEditMilestone(userData);

  const isDraft = form.Status === "Draft";
  const isPublished = form.Status === "Published";
  const readOnly = !canManage || !isDraft;

  const loadVersionHistory = async (id = milestoneId) => {
    if (!userData?.OrganizationId || !id) return;
    try {
      const response = await fetchMilestoneVersionHistory({
        userData,
        milestoneId: id,
      });
      if (response?.success === 1) {
        setVersionHistory(normalizeMilestoneVersionHistory(response));
      } else {
        setVersionHistory([]);
      }
    } catch {
      setVersionHistory([]);
    }
  };

  const loadLookup = async () => {
    if (!userData?.OrganizationId) return;
    try {
      const response = await fetchMilestoneLookup({ userData });
      if (response?.success === 1) {
        setMilestoneLookup(normalizeMilestoneLookup(response));
      }
    } catch {
      setMilestoneLookup([]);
    }
  };

  const loadNotificationRoles = async () => {
    if (!userData?.OrganizationId) {
      setNotificationRolesLoaded(true);
      return;
    }
    try {
      const roles = await fetchRoleLookup(userData);
      if (roles.length) setNotificationRoles(roles);
    } catch {
      setNotificationRoles(NOTIFICATION_ROLE_FALLBACK);
    } finally {
      setNotificationRolesLoaded(true);
    }
  };

  const applyDetailToForm = (detail, notifications, records, roles) => {
    const piRoleId = resolveDefaultPiRoleId(roles);
    let defaultUploaderRoles = resolveRecipientRoleIds(
      detail.DefaultUploaderRoles,
      roles,
    ).join(",");
    if (!String(defaultUploaderRoles || "").trim() && piRoleId) {
      defaultUploaderRoles = piRoleId;
    }

    let defaultSignerRoles = resolveRecipientRoleIds(
      detail.DefaultSignerRoles,
      roles,
    ).join(",");
    const requiresSignature =
      detail.RequiresSignature === true ||
      detail.RequiresSignature === 1 ||
      detail.RequiresSignature === "1";
    if (requiresSignature && !String(defaultSignerRoles || "").trim() && piRoleId) {
      defaultSignerRoles = piRoleId;
    }

    setForm({
      MilestoneId: detail.MilestoneId,
      MilestoneCode: detail.MilestoneCode || "",
      MilestoneName: detail.MilestoneName || "",
      Description: detail.Description || "",
      Category: detail.Category || "Clinical",
      GateCode: detail.GateCode || "PRE_STUDY",
      Tags: detail.Tags || "",
      DefaultFolderName: detail.DefaultFolderName || "",
      DefaultFolderCategory: detail.DefaultFolderCategory
        ? normalizeFolderCategory(detail.DefaultFolderCategory)
        : "eISF",
      DefaultUploaderRoles: defaultUploaderRoles,
      DefaultSignerRoles: defaultSignerRoles,
      RequiresSignature: requiresSignature,
      AllowsNotApplicable: false,
      IsInformational: false,
      records: (records || []).map((row, index) => ({
        RecordId: row.RecordId,
        RecordName: row.RecordName || "",
        RecordType: row.RecordType || "Triggering",
        SortOrder: row.SortOrder ?? index,
      })),
      AnchorType: detail.AnchorType || "ReferenceDate",
      AnchorMilestoneCode: detail.AnchorMilestoneCode || "",
      AnchorOffsetDays:
        detail.AnchorOffsetDays == null
          ? ""
          : formatMilestoneOffsetForForm(detail.AnchorOffsetDays),
      Status: detail.Status,
      Version: detail.VersionNo,
      UsedInPlans: detail.UsedInPlans,
      UsedOnTemplates: detail.UsedOnTemplates,
      UsedInStudies: detail.UsedInStudies,
      IsSystemSource: detail.IsSystemSource,
      DraftMilestoneId: detail.DraftMilestoneId || "",
      RootMilestoneId: detail.RootMilestoneId || detail.MilestoneId,
      SystemDateTime: detail.SystemDateTime,
      notifications:
        notifications?.length > 0
          ? notifications.map((n, index) => ({
              ...normalizeMilestoneNotificationRow(n, index),
              DefaultRecipients: resolveRecipientRoleIds(
                n.DefaultRecipients,
                roles,
              ).join(","),
            }))
          : DEFAULT_NOTIFICATION_RULES(),
    });
    setTagInput("");
  };

  const loadDetail = async (roles = notificationRoles) => {
    if (!userData?.OrganizationId || !milestoneId) return;
    try {
      showLoader("Loading milestone...");
      const response = await fetchMilestoneById({ userData, milestoneId });
      const { detail, notifications, records } = normalizeMilestoneDetail(response);
      if (!detail) throw new Error("Milestone not found.");

      applyDetailToForm(detail, notifications, records, roles);
      await loadVersionHistory(detail.MilestoneId);
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Failed to load milestone.",
        type: "danger",
      });
      setForm(emptyMilestoneForm());
      setVersionHistory([]);
    } finally {
      setLoadAttempted(true);
      hideLoader();
    }
  };

  const loadFolderOptions = async () => {
    if (!userData?.OrganizationId) return;
    try {
      const response = await fetchFolderLibrary({ userData });
      if (isFolderLibrarySuccess(response)) {
        setFolderOptions(folderLibraryToOptions(normalizeFolderLibraryList(response)));
      }
    } catch {
      setFolderOptions([]);
    }
  };

  useEffect(() => {
    if (!userData?.OrganizationId) return;
    setNotificationRolesLoaded(false);
    loadLookup();
    loadNotificationRoles();
    loadFolderOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.OrganizationId]);

  useEffect(() => {
    if (!userData?.OrganizationId || !milestoneId || !notificationRolesLoaded) return;
    loadDetail(notificationRoles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.OrganizationId, milestoneId, notificationRolesLoaded, notificationRoles]);

  useEffect(() => {
    if (!form.DefaultFolderName || !folderOptions.length) return;
    if (form.DefaultFolderCategory && form.DefaultFolderCategory !== "eISF") return;
    setForm((prev) => {
      const nextCategory = resolveFolderCategoryForName(
        prev.DefaultFolderName,
        folderOptions,
      );
      if (prev.DefaultFolderCategory === nextCategory) return prev;
      return { ...prev, DefaultFolderCategory: nextCategory };
    });
  }, [folderOptions, form.DefaultFolderName, form.DefaultFolderCategory, form.MilestoneId]);

  useEffect(() => {
    const el = leftPanelRef.current;
    if (!el) return undefined;

    const syncHeight = () => setLeftPanelHeight(el.offsetHeight);
    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(el);
    window.addEventListener("resize", syncHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", syncHeight);
    };
  }, [form.MilestoneCode, form.MilestoneName, form.Status]);

  const validate = () => {
    const next = {};
    if (!form.MilestoneCode.trim()) next.MilestoneCode = "Code is required.";
    if (!form.MilestoneName.trim()) next.MilestoneName = "Name is required.";
    if (!String(form.GateCode || "").trim()) {
      next.GateCode = "Study gate is required.";
    }

    const folderError = validateMilestoneDefaultFolder(form.DefaultFolderName);
    if (folderError) next.DefaultFolderName = folderError;

    const anchorError = validateMilestoneAnchor(
      form.AnchorType,
      form.AnchorMilestoneCode,
    );
    if (anchorError) next.AnchorMilestoneCode = anchorError;

    const notificationErrors = validateMilestoneNotifications(
      form.notifications,
      notificationRoles,
    );
    if (notificationErrors.length) {
      next.notifications = notificationErrors[0];
    }

    const uploaderError = validateMilestoneUploaderRoles(
      form.DefaultUploaderRoles,
      notificationRoles,
    );
    if (uploaderError) next.DefaultUploaderRoles = uploaderError;

    if (form.RequiresSignature) {
      const signerError = validateMilestoneRoleSelection(
        form.DefaultSignerRoles,
        notificationRoles,
        "signer",
        true,
      );
      if (signerError) next.DefaultSignerRoles = signerError;
    }

    setErrors(next);
    const firstError =
      next.MilestoneCode ||
      next.MilestoneName ||
      next.DefaultFolderName ||
      next.notifications ||
      next.DefaultUploaderRoles ||
      next.DefaultSignerRoles ||
      null;
    return { ok: Object.keys(next).length === 0, firstError };
  };

  const handleSave = async () => {
    if (!isDraft) {
      showAlert({
        title: "Read only",
        message: "Create a draft version before saving changes.",
        type: "warning",
      });
      return;
    }
    if (!canManage) return;
    const { ok, firstError } = validate();
    if (!ok) {
      showAlert({
        title: "Cannot save",
        message: firstError || "Please fix the highlighted fields.",
        type: "warning",
      });
      return;
    }

    const payload = buildMilestoneSavePayload(form);

    try {
      showLoader("Saving...");
      const response = await updateMilestone({ userData, payload });
      if (response?.success === 1) {
        showAlert({ title: "Success", message: "Milestone saved.", type: "success" });
        await loadDetail();
        onChanged?.();
      } else {
        const duplicateError = resolveMilestoneDuplicateCodeError(response);
        if (duplicateError) {
          setErrors((prev) => ({ ...prev, MilestoneCode: duplicateError }));
          showAlert({
            title: "Error",
            message: duplicateError,
            type: "danger",
          });
        } else {
          const message = extractMilestoneApiMessage(response, "Save failed.");
          showAlert({
            title: "Error",
            message,
            type: "danger",
          });
        }
      }
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Save failed.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const handlePublish = async () => {
    if (!form.MilestoneId || !canManage || !isDraft) return;

    const { ok, firstError } = validate();
    if (!ok) {
      showAlert({
        title: "Cannot publish",
        message: firstError || "Please fix the highlighted fields before publishing.",
        type: "warning",
      });
      return;
    }

    const authRes = await openAuthModal({
      showDescription: true,
      requireDescription: true,
    });
    if (authRes?.Success !== 1) return;

    const reason = String(authRes?.Description || "").trim();
    if (!reason) return;

    try {
      showLoader("Publishing...");
      const response = await publishMilestone({
        userData,
        milestoneId: form.MilestoneId,
        reason,
      });
      if (response?.success === 1) {
        showAlert({ title: "Success", message: "Milestone published.", type: "success" });
        await loadDetail();
        onChanged?.();
      } else {
        showAlert({
          title: "Error",
          message: extractMilestoneApiMessage(response, "Publish failed."),
          type: "danger",
        });
      }
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Publish failed.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const handleCreateDraftVersion = async () => {
    if (!form.MilestoneId || !canManage) return;
    try {
      showLoader("Creating draft version...");
      const response = await createMilestoneDraftVersion({
        userData,
        milestoneId: form.MilestoneId,
      });
      const result = extractMilestoneActionResult(response);
      const targetId = result?.MilestoneId;
      if (response?.success === 1 && targetId) {
        showAlert({
          title: "Success",
          message: result?.MSG || "Draft version ready.",
          type: "success",
        });
        if (targetId !== form.MilestoneId) {
          onChanged?.();
          onNavigateMilestone?.(targetId);
        } else {
          await loadDetail();
          onChanged?.();
        }
      } else {
        showAlert({
          title: "Error",
          message: response?.message || "Could not create draft version.",
          type: "danger",
        });
      }
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Could not create draft version.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const openPublishConfirm = () => {
    if (!form.MilestoneId || !canManage || !isDraft) return;
    setConfirmModal({ open: true, action: "publish" });
  };

  const openCreateDraftConfirm = () => {
    if (!form.MilestoneId || !canManage) return;
    setConfirmModal({ open: true, action: "createDraft" });
  };

  const handleConfirmAction = async () => {
    const action = confirmModal.action;
    setConfirmModal({ open: false, action: null });
    if (action === "publish") {
      await handlePublish();
    } else if (action === "createDraft") {
      await handleCreateDraftVersion();
    }
  };

  const updateNotification = (index, field, value) => {
    setForm((prev) => {
      const list = [...(prev.notifications || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, notifications: list };
    });
  };

  const setRecipients = (index, roleIds) => {
    setForm((prev) => {
      const list = [...(prev.notifications || [])];
      list[index] = {
        ...list[index],
        DefaultRecipients: [...new Set((roleIds || []).filter(isRoleGuid))].join(","),
      };
      return { ...prev, notifications: list };
    });
  };

  const setUploaderRoles = (roleIds) => {
    setForm((prev) => ({
      ...prev,
      DefaultUploaderRoles: [...new Set((roleIds || []).filter(isRoleGuid))].join(","),
    }));
  };

  const setSignerRoles = (roleIds) => {
    setForm((prev) => ({
      ...prev,
      DefaultSignerRoles: [...new Set((roleIds || []).filter(isRoleGuid))].join(","),
    }));
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (!tag) return;
    const existing = parseTags(form.Tags);
    if (existing.includes(tag)) {
      setTagInput("");
      return;
    }
    setForm((p) => ({ ...p, Tags: [...existing, tag].join(",") }));
    setTagInput("");
  };

  const removeTag = (tag) => {
    setForm((p) => ({
      ...p,
      Tags: parseTags(p.Tags)
        .filter((t) => t !== tag)
        .join(","),
    }));
  };

  if (!milestoneId) return null;

  if (loadAttempted && !form.MilestoneId) {
    return (
      <div
        className="modal fade show d-block"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog modal-md">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Milestone not found</h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              This milestone could not be loaded. It may have been deleted.
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-primary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const draftId = form.DraftMilestoneId;
  const nextDraftVersion = (form.Version || "1.0").replace(
    /(\d+)$/,
    (_, n) => Number(n) + 1,
  );

  const confirmModalConfig =
    confirmModal.action === "publish"
      ? {
          title: "Publish milestone",
          message: `Are you sure you want to publish milestone "${form.MilestoneCode || ""}" v${form.Version || "1.0"}? Once published, this version can be used in milestone plans.`,
          confirmText: "Publish",
          confirmButtonClass: "btn-success",
          icon: "fas fa-check-circle text-success",
        }
      : confirmModal.action === "createDraft"
        ? {
            title: "Create draft version",
            message: `Create a new draft from "${form.MilestoneCode || ""}" v${form.Version || "1.0"}? A new draft (v${nextDraftVersion}) will be created for editing. The current published version will stay unchanged.`,
            confirmText: "Create draft",
            confirmButtonClass: "btn-primary",
            icon: "fas fa-exclamation-triangle text-warning",
          }
        : null;

  return (
    <div
      className="modal fade show d-block milestone-editor-modal"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog milestone-modal-dialog milestone-modal-dialog--full">
        <div className="modal-content">
          <div className="modal-header milestone-editor-modal__header">
            <div className="milestone-editor-modal__title-wrap">
              <h5 className="modal-title">
                {form.MilestoneCode ? `Edit ${form.MilestoneCode}` : "Edit Milestone"}
              </h5>
              {form.Status ? (
                <span className={statusBadgeClass(form.Status)}>
                  {form.Status}
                  <span className="milestone-status-badge__version">
                    {" "}
                    v{form.Version || "1.0"}
                  </span>
                </span>
              ) : null}
            </div>
            <div className="milestone-editor-modal__header-actions">
              {canManage && isPublished && !draftId ? (
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={openCreateDraftConfirm}
                >
                  <i className="fas fa-copy me-1" />
                  Create draft version
                </button>
              ) : null}
              {canManage && isPublished && draftId ? (
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => onNavigateMilestone?.(draftId)}
                >
                  <i className="fas fa-edit me-1" />
                  Open draft
                </button>
              ) : null}
              {canManage && isDraft ? (
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm"
                  onClick={openPublishConfirm}
                >
                  <i className="fas fa-check-circle me-1" />
                  Publish
                </button>
              ) : null}
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
          </div>
          <div className="modal-body milestone-modal-body">
            {isPublished && canManage ? (
              <div className="milestone-detail-page__published-banner">
                This version is published and read-only. Click{" "}
                <strong>Create draft version</strong> to edit — a new draft (e.g. v
                {(form.Version || "1.0").replace(/(\d+)$/, (_, n) => Number(n) + 1)})
                will be created.
                {draftId ? (
                  <>
                    {" "}
                    A draft is already in progress — use <strong>Open draft</strong>.
                  </>
                ) : null}
              </div>
            ) : null}
            <div className="row g-3 milestone-detail-page__layout">
              <div className="col-xl-9">
                <MilestoneLibraryForm
                  form={form}
                  setForm={setForm}
                  errors={errors}
                  readOnly={readOnly}
                  lockedCategory={lock}
                  notificationRoles={notificationRoles}
                  folderOptions={folderOptions}
                  milestoneLookup={milestoneLookup}
                  tagInput={tagInput}
                  setTagInput={setTagInput}
                  onAddTag={addTag}
                  onRemoveTag={removeTag}
                  onUpdateNotification={updateNotification}
                  onSetRecipients={setRecipients}
                  onSetUploaderRoles={setUploaderRoles}
                  onSetSignerRoles={setSignerRoles}
                  leftPanelRef={leftPanelRef}
                  leftPanelHeight={leftPanelHeight}
                  hideRecordsGrid
                  metaFooter={
                    <MilestoneVersionHistoryPanel
                      form={form}
                      versionHistory={versionHistory}
                      onNavigateVersion={(id) => onNavigateMilestone?.(id)}
                    />
                  }
                />
              </div>
              <div className="col-xl-3">
                <aside className="milestone-detail-sidebar">
                  <div className="milestone-detail-sidebar__card milestone-detail-sidebar__card--records">
                    <MilestoneRecordsGrid
                      variant="sidebar"
                      records={form.records || []}
                      readOnly={readOnly}
                      onChange={(records) => setForm((p) => ({ ...p, records }))}
                    />
                  </div>
                  <MilestoneUsagePanel form={form} />
                </aside>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
              Close
            </button>
            {canManage && isDraft ? (
              <button type="button" className="btn btn-primary" onClick={handleSave}>
                Save draft
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {confirmModalConfig ? (
        <ConfirmModal
          isOpen={confirmModal.open}
          title={confirmModalConfig.title}
          message={confirmModalConfig.message}
          confirmText={confirmModalConfig.confirmText}
          confirmButtonClass={confirmModalConfig.confirmButtonClass}
          icon={confirmModalConfig.icon}
          onConfirm={handleConfirmAction}
          onClose={() => setConfirmModal({ open: false, action: null })}
        />
      ) : null}

      <InfoAlert
        isOpen={alertState.isOpen}
        onClose={hideAlert}
        {...alertState}
      />
    </div>
  );
};

export default MilestoneEditorModal;
